import { CheckoutForm } from '@course/components/checkout';
import { useState } from 'preact/hooks';
import { useAsyncEffect } from 'client/utils/use-async-effect';
import { ProgressBar } from '@components/progress-bar';
import { showError } from '@components/app-error';
import { useIntl } from 'shared/intl/use-intl';
import { PurchaseRow } from 'server/types';
import { useRouter } from '@components/router';
import { SetPasswordForm } from './set-password-form';

type Props = {
  purchase?: PurchaseRow;
  showPasswordPrompt?: boolean;
  /**
   * Set for a checkout pass holder: they are not signed in, so instead of
   * sending them into the course, say where the purchase went.
   */
  checkoutPass?: { email: string };
  provision(): Promise<string>;
};

export function ProvisioningForm(props: Props) {
  const intl = useIntl();
  const [isProcessing, setIsProcessing] = useState(true);
  const [progress, setProgress] = useState(0);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passDone, setPassDone] = useState(false);
  const router = useRouter();
  const { purchase } = props;

  const doRedirect = async (url: string) => {
    if (url) {
      await router.redirectTo(url);
    }
  };

  const provision = async () => {
    try {
      const result = props.provision();
      for (let i = 0; i <= 100; i += 10) {
        setProgress(i);
        await new Promise((r) => setTimeout(r, 50));
      }
      await new Promise((r) => setTimeout(r, 1000));
      const url = await result;
      if (props.checkoutPass) {
        setIsProcessing(false);
        setPassDone(true);
      } else if (props.showPasswordPrompt) {
        setRedirectUrl(url);
        setIsProcessing(false);
        setShowPassword(true);
      } else {
        await doRedirect(url);
      }
    } catch (err) {
      showError(err);
      setIsProcessing(false);
    }
  };

  useAsyncEffect(provision, []);

  if (!purchase) {
    return <p>{intl('Could not find a valid purchase.')}</p>;
  }

  if (isProcessing) {
    return (
      <div class="flex flex-col gap-4">
        <p>{intl('Please wait while we finish setting things up...')}</p>
        <ProgressBar showPercent={false} progress={progress} />
      </div>
    );
  }

  if (passDone && props.checkoutPass) {
    return (
      <div class="flex flex-col gap-4">
        <p class="text-lg font-semibold">{intl("You're all set!")}</p>
        <p>
          {intl('Your purchase has been added to the account for')}{' '}
          <strong>{props.checkoutPass.email}</strong>.{' '}
          {intl("We've emailed a sign-in link to that address.")}
        </p>
        <p class="text-sm text-gray-600">
          <a href="/login" class="text-indigo-600 hover:underline">
            {intl('Sign in')}
          </a>
        </p>
      </div>
    );
  }

  if (showPassword) {
    return (
      <SetPasswordForm
        onComplete={() => doRedirect(redirectUrl)}
        onSkip={() => doRedirect(redirectUrl)}
      />
    );
  }

  return (
    <CheckoutForm actionText={intl('Try Again →')} onSubmit={provision}>
      <p>{intl('An error occurred while setting up your account.')}</p>
    </CheckoutForm>
  );
}
